import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  Chip,
  Stack,
  TextField,
  Switch,
  FormControlLabel,
  Rating,
  Alert,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  Psychology as PsychologyIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  ExpandMore as ExpandMoreIcon,
  ErrorOutline as ErrorOutlineIcon
} from '@mui/icons-material';

/**
 * Get semantic color based on score value (1-5 scale) using theme
 * 1-2: error (poor)
 * 3: warning (acceptable)
 * 4-5: success (excellent)
 */
const getScoreColor = (score, theme) => {
  if (score <= 2) {
    return {
      main: theme.palette.error.main,
      light: theme.palette.error.light,
      dark: theme.palette.error.dark
    };
  }
  if (score === 3) {
    return {
      main: theme.palette.warning.main,
      light: theme.palette.warning.light,
      dark: theme.palette.warning.dark
    };
  }
  return {
    main: theme.palette.success.main,
    light: theme.palette.success.light,
    dark: theme.palette.success.dark
  };
};

/**
 * Get status icon based on score
 */
const getStatusIcon = (score) => {
  if (score <= 2) return <CancelIcon sx={{ fontSize: 20 }} />;
  if (score === 3) return <WarningIcon sx={{ fontSize: 20 }} />;
  return <CheckCircleIcon sx={{ fontSize: 20 }} />;
};

/**
 * Convert 0-1 scale (from backend) to 1-5 scale (for UI)
 */
const convertToUIScale = (value) => {
  if (value === null || value === undefined) return 3; // Default
  return Math.max(1, Math.min(5, Math.round(value * 5)));
};

/**
 * Convert 1-5 scale (from UI) to 0-1 scale (for backend)
 */
const convertToBackendScale = (value) => {
  return value / 5;
};

/**
 * Circular score gauge for overall score (1-5 scale)
 * Visually dominant component showing compliance status
 */
const CircularScoreGauge = ({ score, size = 160 }) => {
  const theme = useTheme();
  const color = getScoreColor(score, theme);
  const isCompliant = score >= 3;
  
  // SVG circle parameters
  const strokeWidth = 12;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  // Convert 1-5 score to percentage for visual representation
  const percentage = ((score - 1) / 4); // 1->0%, 5->100%
  const offset = circumference - (percentage * circumference);

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      {/* SVG Circle */}
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="rgba(255,255,255,0.1)"
          strokeWidth={strokeWidth}
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color.main}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{
            transition: 'stroke-dashoffset 0.6s ease, stroke 0.3s ease'
          }}
        />
      </svg>
      
      {/* Center content - Show score on 1-5 scale */}
      <Box
        sx={{
          position: 'absolute',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Typography
          variant="h2"
          sx={{
            fontWeight: 700,
            color: color.main,
            lineHeight: 1,
            fontSize: size * 0.31
          }}
        >
          {score.toFixed(1)}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            fontWeight: 600,
            fontSize: size * 0.075,
            mt: 0.5
          }}
        >
          / 5
        </Typography>
      </Box>
    </Box>
  );
};

/**
 * Reusable SMART Criterion Card Component
 * Hierarchy: Title → Score → Override Controls → Reasoning (collapsed)
 * Shows model score with optional user override capability
 */
const SmartCriterionCard = ({
  label,
  description,
  modelScore,
  modelReasoning,
  isOverridden,
  overrideScore,
  overrideJustification,
  onOverrideToggle,
  onScoreChange,
  onJustificationChange,
  validationError,
  readOnly = false
}) => {
  const theme = useTheme();
  
  // Local state for override accordion expansion
  const [overrideExpanded, setOverrideExpanded] = useState(false);
  
  // Determine which score to display
  const displayScore = isOverridden ? overrideScore : modelScore;
  const color = getScoreColor(displayScore, theme);
  
  // Handle override toggle - auto-expand when turning on
  const handleOverrideToggle = (checked) => {
    onOverrideToggle(checked);
    if (checked) {
      setOverrideExpanded(true); // Auto-expand when turning on override
    } else {
      setOverrideExpanded(false); // Collapse when turning off override
    }
  };
  
  return (
    <Card
      elevation={0}
      sx={{
        p: 3,
        width: '100%',
        boxSizing: 'border-box',
        bgcolor: isOverridden 
          ? theme.palette.warning.light + '08' 
          : theme.palette.background.paper,
        border: '2px solid',
        borderColor: isOverridden 
          ? theme.palette.warning.main 
          : theme.palette.divider,
        borderRadius: 3,
        transition: 'all 0.3s ease',
        position: 'relative',
        '&:hover': {
          bgcolor: isOverridden 
            ? theme.palette.warning.light + '12' 
            : theme.palette.action.hover,
          borderColor: color.main,
          transform: 'translateY(-2px)',
          boxShadow: `0 8px 24px ${color.main}15`
        }
      }}
    >
      {/* Override badge indicator */}
      {isOverridden && (
        <Chip
          icon={<EditIcon sx={{ fontSize: 14 }} />}
          label="Overridden"
          size="small"
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            bgcolor: theme.palette.warning.main,
            color: theme.palette.common.white,
            fontWeight: 600,
            fontSize: '0.7rem',
            height: 22
          }}
        />
      )}
      
      {/* HIERARCHY LEVEL 1: Criterion Title */}
      <Box mb={2.5}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            mb: 0.5,
            pr: isOverridden ? 10 : 0
          }}
        >
          {label}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {description}
        </Typography>
      </Box>
      
      {/* HIERARCHY LEVEL 2: Primary Score Display */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 3,
          p: 2.5,
          bgcolor: color.light + '20',
          borderRadius: 2,
          border: '1px solid',
          borderColor: color.light
        }}
      >
        {/* Score number in colored box */}
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: 2,
            bgcolor: color.main,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}
        >
          <Typography 
            variant="h4" 
            sx={{ 
              color: theme.palette.common.white, 
              fontWeight: 700 
            }}
          >
            {displayScore}
          </Typography>
        </Box>
        
        {/* Star rating visualization */}
        <Box flex={1}>
          <Rating
            value={displayScore}
            readOnly
            max={5}
            size="large"
            icon={<StarIcon fontSize="inherit" />}
            emptyIcon={<StarBorderIcon fontSize="inherit" />}
            sx={{
              '& .MuiRating-iconFilled': {
                color: color.main
              }
            }}
          />
        </Box>
      </Box>
      
      <Divider sx={{ mb: 3 }} />
      
      {/* HIERARCHY LEVEL 3: Override Controls */}
      <Box mb={3}>
        <FormControlLabel
          control={
            <Switch
              checked={isOverridden}
              onChange={(e) => handleOverrideToggle(e.target.checked)}
              color="warning"
              disabled={readOnly}
            />
          }
          label={
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Override score
            </Typography>
          }
        />
        
        {/* Override interface accordion (conditional) */}
        {isOverridden && (
          <Accordion
            expanded={overrideExpanded}
            onChange={(e, expanded) => setOverrideExpanded(expanded)}
            elevation={0}
            disableGutters
            sx={{
              mt: 2,
              bgcolor: theme.palette.warning.light + '12',
              border: '1px solid',
              borderColor: theme.palette.warning.light,
              borderRadius: '8px !important',
              '&:before': { display: 'none' },
              '&.Mui-expanded': {
                margin: '16px 0 0 0'
              }
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.warning.dark }} />}
              sx={{
                minHeight: 48,
                '&.Mui-expanded': { minHeight: 48 },
                px: 2,
                bgcolor: theme.palette.warning.light + '08',
                '& .MuiAccordionSummary-content': {
                  my: 1.5
                }
              }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <EditIcon sx={{ fontSize: 18, color: theme.palette.warning.main }} />
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.warning.dark
                  }}
                >
                  Override details
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2.5, pb: 2.5, pt: 1.5 }}>
              {/* Score selector */}
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5 }}>
                Select new score
              </Typography>
              <ToggleButtonGroup
                value={overrideScore}
                exclusive
                onChange={(e, newValue) => {
                  if (newValue !== null && !readOnly) onScoreChange(newValue);
                }}
                aria-label="score selection"
                fullWidth
                sx={{ mb: 2.5 }}
                disabled={readOnly}
              >
                {[1, 2, 3, 4, 5].map((score) => {
                  const btnColor = getScoreColor(score, theme);
                  return (
                    <ToggleButton
                      key={score}
                      value={score}
                      sx={{
                        py: 1.5,
                        fontWeight: 600,
                        '&.Mui-selected': {
                          bgcolor: btnColor.main,
                          color: theme.palette.common.white,
                          fontWeight: 700,
                          '&:hover': {
                            bgcolor: btnColor.dark
                          }
                        }
                      }}
                    >
                      {score}
                    </ToggleButton>
                  );
                })}
              </ToggleButtonGroup>
              
              {/* Justification field - REQUIRED */}
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Justification for override"
                placeholder="Explain your reasoning..."
                value={overrideJustification}
                onChange={(e) => onJustificationChange(e.target.value)}
                required
                error={!!validationError}
                helperText={validationError || 'Required: Explain why you are changing this score'}
                disabled={readOnly}
                InputProps={{
                  readOnly: readOnly
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: theme.palette.background.paper
                  }
                }}
              />
            </AccordionDetails>
          </Accordion>
        )}
      </Box>
      
      {/* HIERARCHY LEVEL 4: AI Reasoning (Secondary, Collapsed) */}
      <Accordion
        elevation={0}
        disableGutters
        sx={{
          bgcolor: theme.palette.action.hover,
          border: '1px solid',
          borderColor: theme.palette.divider,
          borderRadius: '8px !important',
          '&:before': { display: 'none' },
          '&.Mui-expanded': {
            margin: 0
          }
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: 'text.secondary' }} />}
          sx={{
            minHeight: 48,
            '&.Mui-expanded': { minHeight: 48 },
            px: 2,
            '& .MuiAccordionSummary-content': {
              my: 1.5
            }
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <PsychologyIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: 'text.secondary'
              }}
            >
              View AI reasoning
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
          {/* AI Reasoning */}
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontStyle: modelReasoning ? 'normal' : 'italic',
              whiteSpace: 'pre-wrap',
              lineHeight: 1.6
            }}
          >
            {modelReasoning || 'No reasoning provided by the model.'}
          </Typography>
          
          {/* User justification - shown below AI reasoning when override is active */}
          {isOverridden && overrideJustification && (
            <Box
              sx={{
                mt: 2.5,
                pt: 2.5,
                borderTop: '2px solid',
                borderColor: theme.palette.warning.light,
                bgcolor: theme.palette.warning.light + '08',
                borderRadius: 1,
                p: 2
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <EditIcon sx={{ fontSize: 16, color: theme.palette.warning.main }} />
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.warning.dark,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5
                  }}
                >
                  User Override Note
                </Typography>
              </Box>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.primary',
                  fontStyle: 'normal',
                  whiteSpace: 'pre-wrap',
                  lineHeight: 1.6,
                  mb: 1
                }}
              >
                {overrideJustification}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontStyle: 'italic',
                  display: 'block',
                  mt: 1
                }}
              >
                — {localStorage.getItem('username') || 'User'}
              </Typography>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
    </Card>
  );
};

/**
 * ValidationDialog - Blocking dialog shown when save is attempted with missing justifications
 * Reusable component for clear validation feedback
 */
const ValidationDialog = ({ open, onClose, missingJustifications, theme }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          border: '2px solid',
          borderColor: theme.palette.error.main
        }
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: theme.palette.error.light + '20',
          borderBottom: '1px solid',
          borderColor: theme.palette.error.light,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          pb: 2
        }}
      >
        <ErrorOutlineIcon sx={{ color: theme.palette.error.main, fontSize: 28 }} />
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.error.dark }}>
            Cannot Save Changes
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Missing required justifications
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <Typography variant="body2" sx={{ mb: 2.5, color: 'text.secondary' }}>
          You have overridden the following criteria without providing justification. 
          Please explain your reasoning for each override before saving.
        </Typography>
        
        <List disablePadding>
          {missingJustifications.map((criterion, index) => (
            <ListItem
              key={index}
              sx={{
                bgcolor: theme.palette.error.light + '08',
                borderRadius: 2,
                mb: 1,
                border: '1px solid',
                borderColor: theme.palette.error.light,
                py: 1.5
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <EditIcon sx={{ color: theme.palette.error.main, fontSize: 20 }} />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {criterion}
                  </Typography>
                }
                secondary="Justification required"
                secondaryTypographyProps={{
                  sx: { fontSize: '0.75rem', color: theme.palette.error.dark }
                }}
              />
            </ListItem>
          ))}
        </List>
        
        <Alert severity="info" sx={{ mt: 3 }}>
          <Typography variant="caption">
            <strong>Why justification matters:</strong> Override justifications create an audit trail 
            for quality assurance and help maintain assessment consistency across the team.
          </Typography>
        </Alert>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 3, pt: 2 }}>
        <Button
          onClick={onClose}
          variant="contained"
          color="primary"
          fullWidth
          sx={{ 
            py: 1.5, 
            fontWeight: 600,
            borderRadius: 2
          }}
        >
          Got it, I'll add justifications
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * COMPACT VARIANT - Refactored for 1-5 scale with override support
 * Optimized for modal dialogs with space constraints
 * Clean hierarchy, no duplicated information
 */
export const SmartAssessmentCompact = ({
  smartSpecific,
  smartMeasurable,
  smartAchievable,
  smartRelevant,
  smartTimeBound,
  smartSpecificReasoning = '',
  smartMeasurableReasoning = '',
  smartAchievableReasoning = '',
  smartRelevantReasoning = '',
  smartTimeBoundReasoning = '',
  smartSpecificOverride = false,
  smartSpecificJustification = '',
  smartMeasurableOverride = false,
  smartMeasurableJustification = '',
  smartAchievableOverride = false,
  smartAchievableJustification = '',
  smartRelevantOverride = false,
  smartRelevantJustification = '',
  smartTimeBoundOverride = false,
  smartTimeBoundJustification = '',
  onSpecificChange,
  onMeasurableChange,
  onAchievableChange,
  onRelevantChange,
  onTimeBoundChange,
  onSpecificOverrideChange,
  onSpecificJustificationChange,
  onMeasurableOverrideChange,
  onMeasurableJustificationChange,
  onAchievableOverrideChange,
  onAchievableJustificationChange,
  onRelevantOverrideChange,
  onRelevantJustificationChange,
  onTimeBoundOverrideChange,
  onTimeBoundJustificationChange,
  readOnly = false
}) => {
  const theme = useTheme();
  
  // Dialog state for validation feedback
  const [validationDialogOpen, setValidationDialogOpen] = useState(false);
  const [missingJustificationsList, setMissingJustificationsList] = useState([]);
  
  // Convert from 0-1 backend scale to 1-5 UI scale
  const [criteria, setCriteria] = useState({
    specific: {
      modelScore: convertToUIScale(smartSpecific),
      modelReasoning: smartSpecificReasoning,
      isOverridden: smartSpecificOverride,
      overrideScore: smartSpecificOverride ? convertToUIScale(smartSpecific) : 3,
      overrideJustification: smartSpecificJustification,
      validationError: ''
    },
    measurable: {
      modelScore: convertToUIScale(smartMeasurable),
      modelReasoning: smartMeasurableReasoning,
      isOverridden: smartMeasurableOverride,
      overrideScore: smartMeasurableOverride ? convertToUIScale(smartMeasurable) : 3,
      overrideJustification: smartMeasurableJustification,
      validationError: ''
    },
    achievable: {
      modelScore: convertToUIScale(smartAchievable),
      modelReasoning: smartAchievableReasoning,
      isOverridden: smartAchievableOverride,
      overrideScore: smartAchievableOverride ? convertToUIScale(smartAchievable) : 3,
      overrideJustification: smartAchievableJustification,
      validationError: ''
    },
    relevant: {
      modelScore: convertToUIScale(smartRelevant),
      modelReasoning: smartRelevantReasoning,
      isOverridden: smartRelevantOverride,
      overrideScore: smartRelevantOverride ? convertToUIScale(smartRelevant) : 3,
      overrideJustification: smartRelevantJustification,
      validationError: ''
    },
    timeBound: {
      modelScore: convertToUIScale(smartTimeBound),
      modelReasoning: smartTimeBoundReasoning,
      isOverridden: smartTimeBoundOverride,
      overrideScore: smartTimeBoundOverride ? convertToUIScale(smartTimeBound) : 3,
      overrideJustification: smartTimeBoundJustification,
      validationError: ''
    }
  });

  // Update criteria when props change (from backend)
  useEffect(() => {
    setCriteria(prev => ({
      ...prev,
      specific: { 
        ...prev.specific, 
        modelScore: convertToUIScale(smartSpecific), 
        modelReasoning: smartSpecificReasoning,
        isOverridden: smartSpecificOverride,
        overrideScore: smartSpecificOverride ? convertToUIScale(smartSpecific) : prev.specific.overrideScore,
        overrideJustification: smartSpecificJustification
      },
      measurable: { 
        ...prev.measurable, 
        modelScore: convertToUIScale(smartMeasurable), 
        modelReasoning: smartMeasurableReasoning,
        isOverridden: smartMeasurableOverride,
        overrideScore: smartMeasurableOverride ? convertToUIScale(smartMeasurable) : prev.measurable.overrideScore,
        overrideJustification: smartMeasurableJustification
      },
      achievable: { 
        ...prev.achievable, 
        modelScore: convertToUIScale(smartAchievable), 
        modelReasoning: smartAchievableReasoning,
        isOverridden: smartAchievableOverride,
        overrideScore: smartAchievableOverride ? convertToUIScale(smartAchievable) : prev.achievable.overrideScore,
        overrideJustification: smartAchievableJustification
      },
      relevant: { 
        ...prev.relevant, 
        modelScore: convertToUIScale(smartRelevant), 
        modelReasoning: smartRelevantReasoning,
        isOverridden: smartRelevantOverride,
        overrideScore: smartRelevantOverride ? convertToUIScale(smartRelevant) : prev.relevant.overrideScore,
        overrideJustification: smartRelevantJustification
      },
      timeBound: { 
        ...prev.timeBound, 
        modelScore: convertToUIScale(smartTimeBound), 
        modelReasoning: smartTimeBoundReasoning,
        isOverridden: smartTimeBoundOverride,
        overrideScore: smartTimeBoundOverride ? convertToUIScale(smartTimeBound) : prev.timeBound.overrideScore,
        overrideJustification: smartTimeBoundJustification
      }
    }));
  }, [
    smartSpecific, smartMeasurable, smartAchievable, smartRelevant, smartTimeBound, 
    smartSpecificReasoning, smartMeasurableReasoning, smartAchievableReasoning, smartRelevantReasoning, smartTimeBoundReasoning,
    smartSpecificOverride, smartMeasurableOverride, smartAchievableOverride, smartRelevantOverride, smartTimeBoundOverride,
    smartSpecificJustification, smartMeasurableJustification, smartAchievableJustification, smartRelevantJustification, smartTimeBoundJustification
  ]);

  // Get effective display score (override or model)
  const getDisplayScore = (criterionKey) => {
    const criterion = criteria[criterionKey];
    return criterion.isOverridden ? criterion.overrideScore : criterion.modelScore;
  };

  // Calculate overall score (average of displayed scores)
  const overallScore = (
    getDisplayScore('specific') +
    getDisplayScore('measurable') +
    getDisplayScore('achievable') +
    getDisplayScore('relevant') +
    getDisplayScore('timeBound')
  ) / 5;

  const isCompliant = overallScore >= 3;
  const color = getScoreColor(overallScore, theme);
  const overrideCount = Object.values(criteria).filter(c => c.isOverridden).length;

  // Handler for override toggle
  const handleOverrideToggle = (criterionKey, isEnabled) => {
    setCriteria(prev => ({
      ...prev,
      [criterionKey]: {
        ...prev[criterionKey],
        isOverridden: isEnabled,
        validationError: ''
      }
    }));

    // Notify parent of override change
    const overrideHandlers = {
      specific: onSpecificOverrideChange,
      measurable: onMeasurableOverrideChange,
      achievable: onAchievableOverrideChange,
      relevant: onRelevantOverrideChange,
      timeBound: onTimeBoundOverrideChange
    };
    if (overrideHandlers[criterionKey]) {
      overrideHandlers[criterionKey](isEnabled);
    }

    // If disabling override, revert to model score and clear justification
    if (!isEnabled) {
      const backendValue = convertToBackendScale(criteria[criterionKey].modelScore);
      const handlers = {
        specific: onSpecificChange,
        measurable: onMeasurableChange,
        achievable: onAchievableChange,
        relevant: onRelevantChange,
        timeBound: onTimeBoundChange
      };
      handlers[criterionKey](backendValue);
      
      // Clear justification
      const justificationHandlers = {
        specific: onSpecificJustificationChange,
        measurable: onMeasurableJustificationChange,
        achievable: onAchievableJustificationChange,
        relevant: onRelevantJustificationChange,
        timeBound: onTimeBoundJustificationChange
      };
      if (justificationHandlers[criterionKey]) {
        justificationHandlers[criterionKey]('');
      }
    }
  };

  // Handler for score change
  const handleScoreChange = (criterionKey, newScore) => {
    setCriteria(prev => ({
      ...prev,
      [criterionKey]: {
        ...prev[criterionKey],
        overrideScore: newScore
      }
    }));

    // Convert to backend scale and update parent
    const backendValue = convertToBackendScale(newScore);
    const handlers = {
      specific: onSpecificChange,
      measurable: onMeasurableChange,
      achievable: onAchievableChange,
      relevant: onRelevantChange,
      timeBound: onTimeBoundChange
    };
    handlers[criterionKey](backendValue);
  };

  // Handler for justification change
  const handleJustificationChange = (criterionKey, value) => {
    setCriteria(prev => ({
      ...prev,
      [criterionKey]: {
        ...prev[criterionKey],
        overrideJustification: value,
        validationError: value.trim() ? '' : 'Justification is required when overriding'
      }
    }));
    
    // Notify parent of justification change
    const justificationHandlers = {
      specific: onSpecificJustificationChange,
      measurable: onMeasurableJustificationChange,
      achievable: onAchievableJustificationChange,
      relevant: onRelevantJustificationChange,
      timeBound: onTimeBoundJustificationChange
    };
    if (justificationHandlers[criterionKey]) {
      justificationHandlers[criterionKey](value);
    }
  };

  // Validate before save (exposed to parent via window for compatibility)
  const validate = () => {
    let isValid = true;
    const newCriteria = { ...criteria };
    const missingJustifications = [];

    Object.keys(newCriteria).forEach(key => {
      if (newCriteria[key].isOverridden && !newCriteria[key].overrideJustification.trim()) {
        newCriteria[key].validationError = 'Justification is required when overriding';
        
        // Build list of missing justifications for dialog
        const criteriaLabels = {
          specific: 'Specific',
          measurable: 'Measurable',
          achievable: 'Achievable',
          relevant: 'Relevant',
          timeBound: 'Time-bound'
        };
        missingJustifications.push(criteriaLabels[key]);
        
        isValid = false;
      }
    });

    setCriteria(newCriteria);
    
    // Show blocking dialog if validation fails
    if (!isValid) {
      setMissingJustificationsList(missingJustifications);
      setValidationDialogOpen(true);
    }
    
    return isValid;
  };

  // Expose validate function to parent
  useEffect(() => {
    window.validateSmartAssessment = validate;
    return () => {
      delete window.validateSmartAssessment;
    };
  }, [criteria]);

  const criteriaConfig = [
    {
      key: 'specific',
      label: 'Specific',
      description: 'Is the requirement clear, well-defined, and unambiguous?'
    },
    {
      key: 'measurable',
      label: 'Measurable',
      description: 'Can compliance be objectively measured or verified?'
    },
    {
      key: 'achievable',
      label: 'Achievable',
      description: 'Is the requirement realistic and technically feasible?'
    },
    {
      key: 'relevant',
      label: 'Relevant',
      description: 'Is it relevant to the project context (electrical infrastructure)?'
    },
    {
      key: 'timeBound',
      label: 'Time-bound',
      description: 'Does it specify when/how long it should be implemented?'
    }
  ];

  return (
    <Stack spacing={3} sx={{ width: '100%', overflow: 'hidden' }}>
      {/* Header */}
      <Box>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            mb: 0.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          SMART Criteria Assessment
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Evaluate each criterion on a scale from 1 to 5
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Overall Score Panel - Simplified, no duplication */}
        <Grid item xs={12} md={4}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              width: '100%',
              boxSizing: 'border-box',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: theme.palette.background.paper,
              border: '2px solid',
              borderColor: color.main,
              borderRadius: 3,
              transition: 'border-color 0.3s ease'
            }}
          >
            <Typography
              variant="overline"
              sx={{
                fontWeight: 700,
                color: 'text.secondary',
                letterSpacing: 1,
                mb: 2
              }}
            >
              Overall Score
            </Typography>
            
            {/* Primary visual: the gauge */}
            <CircularScoreGauge score={overallScore} size={140} />
            
            {/* Compliance status */}
            <Chip
              icon={getStatusIcon(overallScore)}
              label={isCompliant ? 'SMART Compliant' : overallScore <= 2 ? 'Needs Work' : 'Nearly There'}
              sx={{
                mt: 2,
                bgcolor: color.main,
                color: theme.palette.common.white,
                fontWeight: 700,
                fontSize: '0.8rem',
                px: 1,
                '& .MuiChip-icon': {
                  color: theme.palette.common.white
                }
              }}
            />
            
            {/* Threshold indicator - concise */}
            {!isCompliant && (
              <Typography
                variant="caption"
                sx={{
                  mt: 2,
                  color: 'text.secondary',
                  textAlign: 'center',
                  fontWeight: 500
                }}
              >
                {(3 - overallScore).toFixed(1)} below threshold
              </Typography>
            )}
            
            {/* Override count for audit trail */}
            {overrideCount > 0 && (
              <Chip
                label={`${overrideCount} Override${overrideCount > 1 ? 's' : ''}`}
                size="small"
                sx={{
                  mt: 2,
                  bgcolor: theme.palette.warning.main,
                  color: theme.palette.common.white,
                  fontWeight: 600
                }}
              />
            )}
          </Paper>
        </Grid>

        {/* Individual Criteria - Right panel */}
        <Grid item xs={12} md={8}>
          <Stack spacing={3} sx={{ width: '100%' }}>
            {criteriaConfig.map(({ key, label, description }) => (
              <SmartCriterionCard
                key={key}
                label={label}
                description={description}
                modelScore={criteria[key].modelScore}
                modelReasoning={criteria[key].modelReasoning}
                isOverridden={criteria[key].isOverridden}
                overrideScore={criteria[key].overrideScore}
                overrideJustification={criteria[key].overrideJustification}
                onOverrideToggle={(enabled) => handleOverrideToggle(key, enabled)}
                onScoreChange={(score) => handleScoreChange(key, score)}
                onJustificationChange={(value) => handleJustificationChange(key, value)}
                validationError={criteria[key].validationError}
                readOnly={readOnly}
              />
            ))}
          </Stack>
          
          {/* Validation summary - only shown when errors exist */}
          {Object.values(criteria).some(c => c.validationError) && (
            <Alert severity="error" sx={{ mt: 3 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Validation errors:
              </Typography>
              <ul style={{ margin: '8px 0 0 20px', padding: 0 }}>
                {criteriaConfig
                  .filter(({ key }) => criteria[key].validationError)
                  .map(({ key, label }) => (
                    <li key={key}>
                      <Typography variant="caption">
                        {label}: {criteria[key].validationError}
                      </Typography>
                    </li>
                  ))}
              </ul>
            </Alert>
          )}
        </Grid>
      </Grid>
      
      {/* Validation Dialog - shown when save is blocked */}
      <ValidationDialog
        open={validationDialogOpen}
        onClose={() => setValidationDialogOpen(false)}
        missingJustifications={missingJustificationsList}
        theme={theme}
      />
    </Stack>
  );
};

export default SmartAssessmentCompact;
